import { useMutation, useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import Toaster from 'components/Toaster';
import {
  PageShell,
  PageHeader,
  DataTable,
  SearchInput,
  AddButton,
  SegmentedFilter,
  ActionIcon,
  StatusPill,
  EntityCell,
  ConfirmModal,
  tokens,
} from 'components/v3';
import { query as GET_SERIES_ADMIN } from './gql/queries/get-series';
import SeriesDetailModal from './SeriesDetailModal';

// ============================================================
// Gen-3 Series — composed from v3 components, wired to getSeriesAdmin.
// Mirrors the Tracks page (src/pages/app/Tracks/index.js).
// ============================================================

const LIMIT = 15;

const DELETE_SERIES = gql`
  mutation DeleteSeries($id: Int!) {
    deleteSeries(id: $id)
  }
`;

// SegmentedFilter key -> backend SeriesAdminFilter enum.
const FILTER_ENUM = { live: 'LIVE', active: 'ACTIVE', inactive: 'INACTIVE' };

const COLUMNS = [
  {
    key: 'series',
    label: 'Series',
    width: 'minmax(300px,1.8fr)',
    primary: true,
    skeleton: 'media',
  },
  { key: 'type', label: 'Series Type', width: 'minmax(130px,1fr)' },
  { key: 'events', label: 'Events', width: '84px' },
  { key: 'status', label: 'Status', width: '120px', skeleton: 'pill' },
  {
    key: 'actions',
    label: 'Actions',
    width: '124px',
    align: 'right',
    skeleton: 'actions',
    actionsCount: 4,
  },
];

// Status badge: LIVE when there are published events this year, else
// Active/Inactive per the series' `active` flag.
function renderStatus(series) {
  if (series.eventsCount > 0) {
    return (
      <StatusPill
        label="Live"
        color="#1F9D57"
        background="rgba(52,210,123,0.13)"
        border="rgba(52,210,123,0.34)"
      />
    );
  }
  if (series.active === false) {
    return (
      <StatusPill
        label="Inactive"
        color="#64748b"
        background="rgba(100,116,139,0.13)"
        border="rgba(100,116,139,0.34)"
      />
    );
  }
  return (
    <StatusPill
      label="Active"
      color="#2C6FD6"
      background="rgba(44,111,214,0.13)"
      border="rgba(44,111,214,0.34)"
    />
  );
}

const Series = () => {
  const t = tokens(useTheme());
  const history = useHistory();
  const [filter, setFilter] = useState('live');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [confirmSeries, setConfirmSeries] = useState(null);
  const [viewSeries, setViewSeries] = useState(null);
  const [toasterData, setToasterData] = useState(null);

  const input = {
    filter: FILTER_ENUM[filter],
    page: String(page),
    limit: String(LIMIT),
  };
  if (search) input.queryString = search;

  const { data, loading, refetch } = useQuery(GET_SERIES_ADMIN, {
    variables: { input },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [deleteSeries, { loading: deleting }] = useMutation(DELETE_SERIES);

  const result = data && data.getSeriesAdmin;
  const series = (result && result.results) || [];
  const counts = (result && result.counts) || {
    live: 0,
    active: 0,
    inactive: 0,
  };
  const total = (result && result.count) || 0;
  const pageCount = Math.max(1, Math.ceil(total / LIMIT));

  const onFilter = (key) => {
    setFilter(key);
    setPage(1);
  };
  const onSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleDelete = async () => {
    const item = confirmSeries;
    if (!item) return;
    try {
      const res = await deleteSeries({
        variables: { id: parseInt(item.id, 10) },
      });
      if (res && res.data && res.data.deleteSeries) {
        setToasterData({
          title: 'Series deleted',
          message: `${item.name} has been deleted.`,
          type: 'success',
        });
        await refetch();
      } else {
        throw new Error('delete failed');
      }
    } catch (e) {
      setToasterData({
        title: 'Delete failed',
        message: `We couldn't delete ${item.name}. Please try again.`,
        type: 'error',
      });
    } finally {
      setConfirmSeries(null);
    }
  };

  const renderRow = (s) => ({
    series: <EntityCell logo={s.logo} code={s.short_name} title={s.name} />,
    type: (s.type && (s.type.key || s.type.value)) || '',
    events: (
      <span
        style={{
          fontSize: '14.5px',
          fontWeight: 600,
          color: s.eventsCount ? t.text : t.faint,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {s.eventsCount}
      </span>
    ),
    status: renderStatus(s),
    actions: (
      <div style={{ display: 'flex', gap: 3 }}>
        <ActionIcon icon="eye" title="View" onClick={() => setViewSeries(s)} />
        <ActionIcon
          icon="calendar"
          title="Events"
          onClick={() => history.push(`/admin/series/events/${s.id}`)}
        />
        <ActionIcon
          icon="square-pen"
          title="Edit"
          onClick={() => history.push(`/admin/series/editSeries/${s.id}`)}
        />
        <ActionIcon
          icon="trash-2"
          title="Delete"
          onClick={() => setConfirmSeries(s)}
        />
      </div>
    ),
  });

  return (
    <PageShell>
      <PageHeader icon="flag" title="Series" />

      <DataTable
        columns={COLUMNS}
        data={series}
        renderRow={renderRow}
        loading={loading}
        emptyLabel="No series in this view."
        page={page}
        pageCount={pageCount}
        onPageChange={setPage}
        toolbar={
          <>
            <SearchInput
              onChange={onSearch}
              placeholder="Search series..."
              debounce={350}
            />
            <AddButton title="Add Series" to="/admin/series/addSeries" />
          </>
        }
        filter={
          <SegmentedFilter
            value={filter}
            onChange={onFilter}
            options={[
              {
                key: 'live',
                label: 'Live Events',
                count: counts.live,
                dot: true,
                dotColor: '#1F9D57',
              },
              { key: 'active', label: 'Active', count: counts.active },
              { key: 'inactive', label: 'Inactive', count: counts.inactive },
            ]}
          />
        }
      />

      <SeriesDetailModal
        series={viewSeries}
        statusNode={viewSeries ? renderStatus(viewSeries) : null}
        onEdit={() =>
          viewSeries &&
          history.push(`/admin/series/editSeries/${viewSeries.id}`)
        }
        onClose={() => setViewSeries(null)}
      />

      <ConfirmModal
        open={!!confirmSeries}
        title="Delete this series?"
        message={
          confirmSeries
            ? `${confirmSeries.name} will be permanently deleted. This can't be undone.`
            : ''
        }
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmSeries(null)}
      />

      {toasterData && (
        <Toaster
          isOpen
          title={toasterData.title}
          message={toasterData.message}
          type={toasterData.type}
          onClose={() => setToasterData(null)}
        />
      )}
    </PageShell>
  );
};

export default Series;
